import {Component, OnInit} from '@angular/core';
import {
    IOrderHistoryView,
    IOrderHistoryViewProduct,
    IOrderHistoryViewProductDisplayAttribute,
    IOrderViewPackslip
} from '../orders-models';
import {BaseComponent} from '../../base.component';
import {UserService} from '../../common/user.service';
import {LoggerService} from '../../common/logger.service';
import {ActivatedRoute, Router} from "@angular/router";
import {Constants} from "../../constants";
import {find} from "lodash";
import {OrderHistoryCacheService} from "../order-history/order-history-cache.service";
import {GqlOrderHistoryService} from "../../../checkout/graphql/order-history/gql-order-history.service";
import {OrderHistoryService} from "../order-history/order-history.service";
import {TranslateService} from "../../translation/translate.service";
import {Util} from "../../util/util";

@Component({
    selector: 'order-history-details',
    templateUrl: './order-history-details.component.html',
    styleUrls: ['../orders.scss', './order-history-details.component.scss'],
    standalone: false
})
export class OrderHistoryDetailsComponent extends BaseComponent implements OnInit {
    orderView: IOrderHistoryView;
    loading: boolean = false;
    cashPSText: string;
    loadingPackslip: boolean = false;
    showPackslipMsg: boolean = false;

	headerLabels = [
		'cart.order.history.product.price',
		'cart.order.history.product.volume',
		'cart.order.history.product.quantity',
		'cart.order.history.product.total.price',
		'cart.order.history.order.total.volume'
	];

	productDisplayAttributes: IOrderHistoryViewProductDisplayAttribute[] = [
        {name: 'price', isCurrency: true},
        {name: 'volume', isCurrency: false},
        {name: 'quantity', isCurrency: false},
        {name: 'totalPrice', isCurrency: true},
        {name: 'totalVolume', isCurrency: false}
	];

	constructor(
		private userService: UserService,
		private loggerService: LoggerService,
        private orderHistoryService: OrderHistoryService,
        private orderHistoryCacheService: OrderHistoryCacheService,
        private gqlOrderHistoryService: GqlOrderHistoryService,
        private translateService: TranslateService,
        private route: ActivatedRoute,
        private router: Router
	) {
		super(userService, loggerService);
	}

	ngOnInit() {
        const orderId = this.route.snapshot.paramMap.has(Constants.ORDER_ID) ? parseInt(this.route.snapshot.paramMap.get(Constants.ORDER_ID)) : null;
        if (!orderId) { // route guard should handle this, but just in case
            this.router.navigate([Constants.ORDER_HISTORY_STATE]);
        } else {
            this.loadActiveOrderView(orderId);
        }
	}

    get hasAutoOrderDiscount(): boolean {
        return !Util.isEmpty(this.orderView?.autoOrderDiscount) && this.orderView.autoOrderDiscount != 0;
    }

    get hasOrderReward(): boolean {
        return !Util.isEmpty(this.orderView?.totalIoReward) && this.orderView.totalIoReward != 0;
    }

    get subtotal(): number {
        return this.showProductCreditTaxDisclaimer ? this.orderView?.exclusiveTotalPrice : this.orderView?.totalPrice;
    }

    get hasOrderTax(): boolean {
        return this.orderTax > 0;
    }

    get orderTax(): number {
        if (this.showProductCreditTaxDisclaimer) {
            return this.orderView?.totalTaxes;
        }
        if (!this.orderView?.taxInclusive) {
            return this.orderView?.taxCost ?? this.orderView?.totalTaxes;
        }
        return 0;
    }

    get showProductCreditTaxDisclaimer(): boolean {
        // if tax inclusive and product credits applied, we want to separate taxes from their totals and show the tax exclusive values
        return this.orderView?.taxInclusive && this.orderView?.vouchersPayments?.length > 0 && !this.userService.getShopUserSnapshot().config.voucherCoverTaxAndShipping;
    }

    get trackingCount(): number {
        return this.orderHistoryService.getTrackingCount(this.orderView);
    }

    get isShippingCostExclusiveDisplayable(): boolean {
        return this.orderView?.taxExempt || !this.orderView?.shippingTaxInclusive;
    }

    get hasOrderHandling(): boolean {
        return this.orderView?.totalHandling > 0;
    }

    loadActiveOrderView(orderId: number) {
        if (this.orderHistoryCacheService.getOrderViews()) {
            const orderView = find(this.orderHistoryCacheService.getOrderViews(), (orderView: IOrderHistoryView) => orderView.id === orderId);
            if (orderView) {
                this.setActiveOrderView(orderView);
            }
        }
        if (!this.orderView) {
            this.getOrderView(orderId);
        }
    }

    getOrderView(orderId: number) {
        this.loading = true;
        this.gqlOrderHistoryService.getOrderView(orderId).subscribe({
            next: (orderView: IOrderHistoryView) => {
                this.setActiveOrderView(orderView);
                this.loading = false;
            },
            error: (error: any) => {
                this.log.error(`Error fetching order history for order id: ${orderId}`, error);
                this.loading = false;
            }
        });
    }

    setActiveOrderView(orderView: IOrderHistoryView) {
        this.orderView = orderView;
        this.orderHistoryCacheService.setActiveOrderView(orderView);
        this.setCashPSText();
    }

    setCashPSText() {
        if (this.orderView.bpsReference?.cashPSAccountNumber) {
            this.translateService.translateRequest({
                tag: 'cart.cashpayment.instruction',
                replacements: new Map([['AccountNumber', this.orderView.bpsReference?.cashPSAccountNumber]])
            }).then((data: string) => this.cashPSText = data);
        }
    }

    addItemToCart(product: IOrderHistoryViewProduct, orderId: number) {
        this.orderHistoryCacheService.addProductAdding(product, orderId);
        this.orderHistoryService.addItemToCart(product, orderId);
    }

	showPackslipBtn(packslips: IOrderViewPackslip[]): boolean {
		return !!packslips && packslips.length > 0;
	}

	showPackslip(warehouseId: string, packslipId: number, orderId: number) {
        if (!this.loadingPackslip) {
            this.loadingPackslip = true;
            this.showPackslipMsg = false;
            this.gqlOrderHistoryService.getPackslipPdf(warehouseId, packslipId, orderId).subscribe({
                next: (response: string) => {
                    const success: boolean = this.createPdf(response);
                    this.showPackslipMsg = !success;
                    this.loadingPackslip = false;
                },
                error: (error: any) => {
                    this.log.error('showPackslip', error);
                    this.loadingPackslip = false;
                }
            });
        }
	}

    createPdf(base64String: string): boolean {
        const cleanedUpString = JSON.parse(base64String);
        const binaryData = atob(cleanedUpString);

        // Convert binary data to an array of 8-bit unsigned integers
        const byteArray = new Uint8Array(binaryData.length);
        for (let i = 0; i < binaryData.length; i++) {
            byteArray[i] = binaryData.charCodeAt(i);
        }

        // Create a Blob object with the binary data and MIME type
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        // Create a URL for the Blob and open it in a new window
        const pdfUrl = window.URL.createObjectURL(blob);

        // handle possible popup blocker
        const newWindow = window.open('', '_blank');
        if (!!newWindow && newWindow.location.href === 'about:blank') {
            newWindow.location.href = pdfUrl;
            return true;
        }
        return false;
    }
}
